import { UniversalSearch } from '@civilgrid/ui/components';
import { KnockFeedProvider, NotificationCell, NotificationFeedPopover, NotificationIconButton } from '@knocklabs/react';
import { Link, useMatchRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import Icon from '~/components2/Icon';
import logoImage from '../../../assets/img/logo/lettermarkOrangeLG.png';
import { NewProjectButton } from './NewProject';
import { OrgSwitcher, ProjectSwitcher } from './OrgProjectSwitcher';
import { AdvancedSearchDialog } from './UniversalSearch/AdvancedSearchDialog';
import { useUniversalSearch } from './UniversalSearch/useUniversalSearch';
import UserSettings from './UserSettings';

import '@knocklabs/react/dist/index.css';
import { useSnapshot } from 'valtio';
import CloseIconButton from '~/components2/forms/CloseIconButton';
import splitViewStore from '~/components2/Map/Widgets/SplitView/store';
import getConfig from '~/config';
import rootStore from '~/store';
import HelpDropdown from './HelpDropdown';
import PageSwitcher from './PageSwitcher';
import TopNavPreviewEnvBadge from './PreviewEnvBadge';

export interface TopNavProps {
  includeOrgProjectSwitcher?: boolean;
  includeSearch?: boolean;
  includeNewProjectButton?: boolean;
}

export default function TopNav({ includeOrgProjectSwitcher, includeSearch, includeNewProjectButton }: TopNavProps) {
  const notifButtonRef = useRef(null);
  const [isFeedVisible, setIsFeedVisible] = useState(false);
  const [showProductUpdates, setShowProductUpdates] = useState(false);
  const config = getConfig();
  const { me } = useSnapshot(rootStore);
  const { splitView } = useSnapshot(splitViewStore);
  const search = useUniversalSearch();
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);

  const matchRoute = useMatchRoute();
  const inProjectDetail = Boolean(
    matchRoute({ to: '/projects/$projectId' }) ||
      matchRoute({ to: '/projects/$projectId/$tabId' }) ||
      matchRoute({ to: '/projects/$projectId/$tabId/$mapId' }) ||
      matchRoute({ to: '/conflictsSummary' }) ||
      matchRoute({ to: '/dataRequestFulfillment' }),
  );

  return (
    <div
      id="top-nav"
      className="flex h-top-nav-height w-full flex-none content-center items-center justify-between border-b bg-white px-2"
    >
      <nav aria-label="Primary" className="flex grow basis-0 items-center justify-start space-x-2">
        <Link to="/" data-testid="top-nav-logo-link" className="inline-flex rounded-[6px] focus-ring">
          <div className="flex items-center">
            <img src={logoImage} alt="CivilGrid Logo" className="w-36" />
          </div>
        </Link>
        <PageSwitcher />
      </nav>
      {includeOrgProjectSwitcher && me && (
        <div className="flex items-center justify-center h-7">
          <OrgSwitcher inProjectDetail={inProjectDetail} />
          {inProjectDetail && (
            <>
              <div className="mx-2">/</div>
              <ProjectSwitcher />
            </>
          )}
        </div>
      )}
      <div className="flex grow basis-0 flex-row items-center justify-end gap-x-2">
        <TopNavPreviewEnvBadge />
        {includeSearch && !splitView && (
          <>
            <search>
              <UniversalSearch
                ref={searchTriggerRef}
                data-testid="top-nav-universal-search-trigger"
                getItems={search.getItems}
                minSearchLength={3}
                onOpen={search.onOpen}
                emptyHint="Search for an address or a project."
                emptyAction={{
                  label: 'Advanced Search',
                  icon: <Icon icon="advancedSearch" size={14} />,
                  onSelect: () => setAdvancedSearchOpen(true),
                  'data-testid': 'top-nav-advanced-search-trigger',
                }}
              />
            </search>
            <AdvancedSearchDialog
              open={advancedSearchOpen}
              onOpenChange={setAdvancedSearchOpen}
              returnFocusTo={searchTriggerRef}
            />
          </>
        )}
        {includeNewProjectButton && <NewProjectButton data-testid="top-nav-new-project" />}

        <KnockFeedProvider feedId={config.knockWebChannelId}>
          <span
            className="inline-flex [--rnf-notification-icon-button-size:28px] [&>button]:flex! [&>button]:items-center [&>button]:justify-center [&>button]:rounded-[6px] [&>button]:focus-ring"
            data-testid="top-nav-notifications"
          >
            <NotificationIconButton
              onClick={() => {
                // The Product Updates modal blocks pointer events on everything outside it, so the two
                // panels must be mutually exclusive; opening notifications closes Product Updates.
                setShowProductUpdates(false);
                setIsFeedVisible((prev) => !prev);
              }}
              ref={notifButtonRef}
            />
          </span>

          <NotificationFeedPopover
            renderItem={({ item, ...props }) => <NotificationCell key={item.id} {...props} item={item} avatar={null} />}
            renderHeader={() => (
              <div className="flex items-baseline justify-between px-4 py-2 font-bold">
                <div>Notifications</div>
                <CloseIconButton
                  aria-label="Close notifications"
                  data-testid="top-nav-notifications-close"
                  onClick={() => setIsFeedVisible(false)}
                />
              </div>
            )}
            isVisible={isFeedVisible}
            buttonRef={notifButtonRef}
            // no idea why this prop is required, might be a knock bug
            onClose={() => {}}
          />
        </KnockFeedProvider>

        <HelpDropdown
          productUpdatesOpen={showProductUpdates}
          onProductUpdatesOpenChange={(open) => {
            setShowProductUpdates(open);
            // Opening Product Updates closes the notifications feed so the modal never traps it open.
            if (open) setIsFeedVisible(false);
          }}
        />
        <UserSettings />
      </div>
    </div>
  );
}
